import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Avatar,
  Grid,
  Chip,
  Divider,
  CircularProgress,
  alpha,
  useTheme,
  Stack,
  Tooltip
} from "@mui/material";
import {
  Close as CloseIcon,
  Phone as PhoneIcon,
  WhatsApp as WhatsAppIcon,
  LocationOn as LocationIcon,
  BusinessCenter as BusinessIcon,
  DateRange as DateIcon,
  AssignmentInd as AssignmentIcon,
  School as SchoolIcon,
  Wc as GenderIcon,
  OpenInNew as OpenInNewIcon,
  PersonPinCircleOutlined
} from "@mui/icons-material";
import useAxiosPrivate from "app/hooks/useAxiosPrivate";
import { BASE_URL } from "app/api/axios";
import moment from "moment";

export default function ReferenceDetailsDialog({ open, onClose, referenceId }) {
  const theme = useTheme();
  const axiosPrivate = useAxiosPrivate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    let isMounted = true;
    if (open && referenceId) {
      setLoading(true);
      setData(null);
      axiosPrivate
        .post("GetReferenceByIDNew.php", { UID: referenceId })
        .then((res) => {
          if (isMounted && res.data && res.data.status === 1) {
            setData(res.data.Data[0]);
          }
        })
        .catch((err) => console.error(err))
        .finally(() => {
          if (isMounted) setLoading(false);
        });
    }
    return () => {
      isMounted = false;
    };
  }, [open, referenceId, axiosPrivate]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{
      sx: {
        borderRadius: 3,
        background: alpha(theme.palette.background.paper, 0.9),
        backdropFilter: "blur(10px)",
        boxShadow: theme.shadows[10],
      }
    }}>
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" fontWeight="bold">Reference Details</Typography>
        <Stack direction="row" spacing={1}>
          {data && (
            <Tooltip title={
              (data.IsProspect === "YES" || data.IsProspect === "Yes" || data.IsProspect === 1 || data.IsProspect === "1" || data.IsProspect === true)
                ? "View Complete Prospect"
                : "View Complete Reference"
            }>
              <IconButton
                component="a"
                href={
                  (data.IsProspect === "YES" || data.IsProspect === "Yes" || data.IsProspect === 1 || data.IsProspect === "1" || data.IsProspect === true)
                    ? `/Prospects/ViewProspect/${referenceId}`
                    : `/References/ViewReference/${referenceId}`
                }
                target="_blank"
                size="small"
                color="primary"
              >
                <OpenInNewIcon />
              </IconButton>
            </Tooltip>
          )}
          <IconButton onClick={onClose} size="small" sx={{ color: theme.palette.grey[500] }}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ p: 3, minHeight: 200, display: 'flex', flexDirection: 'column' }}>
        {loading ? (
          <Box flex={1} display="flex" alignItems="center" justifyContent="center">
            <CircularProgress />
          </Box>
        ) : data ? (
          <Box>
            <Box display="flex" alignItems="center" gap={2} mb={3}>
              <Avatar
                src={data.Picture ? `${BASE_URL}/GetSecureDoc?type=profile&path=${encodeURIComponent(data.Picture)}` : ""}
                sx={{ width: 80, height: 80, boxShadow: theme.shadows[3] }}
              />
              <Box>
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  {data.Salutation ? `${data.Salutation} ` : ""}{data.Name}
                </Typography>
                <Stack direction="row" spacing={1}>
                  {data.Status && <Chip label={data.Status} size="small" color={data.Status === 'ACTIVE' ? 'success' : 'default'} variant="outlined" />}
                  {data.Category && <Chip label={data.Category} size="small" color="primary" variant="outlined" />}
                </Stack>
              </Box>
            </Box>

            <Grid container spacing={2}>
              {data.MobileNo && (
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <PhoneIcon color="action" fontSize="small" />
                    <Typography variant="body2">{data.MobileNo}</Typography>
                  </Box>
                </Grid>
              )}
              {data.WhatsAppNo && (
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <WhatsAppIcon color="success" fontSize="small" />
                    <Typography variant="body2">{data.WhatsAppNo}</Typography>
                  </Box>
                </Grid>
              )}
              {data.Caste && (
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <PersonPinCircleOutlined color="error" fontSize="small" />
                    <Typography variant="body2">{data.Caste}</Typography>
                  </Box>
                </Grid>
              )}
              {data.Profession && (
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <BusinessIcon color="primary" fontSize="small" />
                    <Typography variant="body2">{data.Profession} {data.WorkType ? `(${data.WorkType})` : ''}</Typography>
                  </Box>
                </Grid>
              )}
              {data.DOB && data.DOB !== "0000-00-00" && (
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <DateIcon color="secondary" fontSize="small" />
                    <Typography variant="body2">DOB: {moment(data.DOB).format("DD MMM YYYY")}</Typography>
                  </Box>
                </Grid>
              )}
              {data.RefBy && (
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <AssignmentIcon color="action" fontSize="small" />
                    <Typography variant="body2">Ref By: {data.RefByName || data.RefBy}</Typography>
                  </Box>
                </Grid>
              )}
            </Grid>

            {(data.Address || data.Village || data.Tehsil || data.District || data.State || data.PIN) && (
              <Box mt={3}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>Address</Typography>
                <Typography variant="body2">{data.Address} {data.Village} {data.Tehsil} {data.District} {data.State} {data.PIN}</Typography>
              </Box>
            )}

            {data.Other && (
              <Box mt={2}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>Other Information</Typography>
                <Typography variant="body2">{data.Other}</Typography>
              </Box>
            )}
          </Box>
        ) : (
          <Box flex={1} display="flex" alignItems="center" justifyContent="center">
            <Typography color="text.secondary">No details found or failed to load.</Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
